import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled, { css } from 'styled-components';

import { tooltipClasses } from '@mui/material';
import { TooltipProps } from '@mui/material/Tooltip/Tooltip';
import { styled as muiStyled } from '@mui/material/styles';
import {
  StyledComponentBase, // @ts-ignore
} from '@types/styled-components';
import {
  Box,
  Button,
  LoadingButton,
  MUITable,
  Menu,
  PageHeader,
  Skeleton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabelMUI,
  Tooltip,
  Typography,
} from 'components';
import {
  Add,
  CloseCircle,
  Copy,
  DocumentDownload,
  Filter,
  FilterRemove,
  TickCircle,
} from 'iconsax-react';

import { AdvancedDateFilter } from './Filtering/AdvancedDateFilter';
import { AdvancedNumericFilter } from './Filtering/AdvancedNumericFilter';
import { AdvancedStringFilter } from './Filtering/AdvancedStringFilter';
import { StringFilter } from './Filtering/StringFilter';
import { getOptionColumn } from './TableMenu';
import { TableSettings } from './TableSettings';
import { FilterValue, TableColumn } from './types';

import { _, classNames, useCallbackRef, useTranslation } from 'third-party';

import {
  INFINITE_SCROLL_BOTTOM_OFFSET_PX,
  TOOLTIP_APPEAR_DELAY,
} from 'constants/common';

import { hasActiveColumnFilter } from 'utils/app';
import { isTitleString } from 'utils/helper';

import { APISortOrder } from 'types/api';
import { TableSettingsT } from 'types/app';

const COLUMN_RESIZER_WIDTH = 20;
const SCROLL_WIDTH = 30;
const SPAN_COLUMN_ID = 'span-column';

const fixedStyles = {
  position: 'sticky',
  left: 0,
  backgroundColor: 'white',
  zIndex: 3,
};

const emptyTableSortHeaderStyles = {
  textDecoration: 'none',
  pointerEvents: 'none',
};

export const TableSortLabel = styled(TableSortLabelMUI)`
  font-weight: 300;
  font-size: 0.9rem !important;
  line-height: 1.75;
  text-decoration: none;
  text-transform: uppercase;

  &.Mui-active,
  &:hover {
    color: ${props => props.theme.custom.palette.secondary900};
    .MuiTableSortLabel-icon {
      opacity: 1;
    }
  }
  color: ${props => props.theme.custom.palette.primary900};
  .MuiTableSortLabel-icon {
    fill: ${props => props.theme.custom.palette.secondary900};
    stroke: ${props => props.theme.custom.palette.secondary900};
  }
`;

const CellTooltip = muiStyled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    whiteSpace: 'pre-line',
  },
}));

const TableWrapper = styled.div<{ minheight: string }>`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: ${props => props.minheight || '0'};
`;

const StyledTableContainer = styled(TableContainer)`
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

export const StyledTable = styled(MUITable)`
  table-layout: fixed;
`;

export const StyledTableBodyCell = styled(TableCell)<{ height: string }>`
  height: ${props => props.height};
  padding: 0 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 0;
  box-sizing: border-box;
`;

const StyledTableBody = styled(TableBody)`
  flex-grow: 1;
`;

const StyledTableHeaderCell = styled(TableCell)<{ headerheight: string }>`
  height: ${props => props.headerheight};
  padding: 0 1.6rem;
  border-bottom: 1px solid ${props => props.theme.custom.palette.borderColor};
  border-top: 1px solid ${props => props.theme.custom.palette.borderColor};
  white-space: nowrap;
  box-sizing: border-box;
  z-index: 3;
`;

export const StyledTableColumnLabel = styled(Typography)`
  text-transform: uppercase;
  color: ${props => props.theme.custom.palette.primary900};
`;

const StyledTableRow = styled(TableRow)<{ rowhighlightcolor: string }>`
  border: 1px solid ${props => props.theme.custom.palette.borderColor};
  padding: 0;
  height: 1rem;
  background-color: ${props => props.rowhighlightcolor || 'white'};
  &:not(.MuiTableRow-head):nth-of-type(even) {
    background-color: ${props =>
      props.rowhighlightcolor || props.theme.custom.palette.primary0};
  }
  &:not(.MuiTableRow-head) {
    &.active,
    &:hover {
      cursor: pointer;
    }

    &.active {
      background-color: ${props => props.theme.custom.palette.primary50};
    }

    &:hover {
      background-color: ${props => props.theme.custom.palette.primary50};
    }
  }
`;

const StyledTableSection = styled(TableCell)<{ top: string; height: string }>`
  border: 1px solid #d4d4d4;
  padding: 0 1.6rem;
  background-color: ${props => props.theme.custom.palette.secondary50};
  position: sticky;
  height: 4rem;
  top: ${props => props.top};
  z-index: 4;
`;

const TableBodySectionRow = styled(TableRow)``;

const StyledTableSectionText = styled(Typography)`
  color: ${props => props.theme.custom.palette.secondary900};
`;

const StyledTableBodyCellInner = styled(Typography)`
  font-size: 0.8rem !important;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const EmptyResultRow = styled(TableRow)`
  padding: 1rem 0;
`;

const EmptyResultCell = styled(TableCell)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.custom.palette.muted800};
  border: none;
  text-align: center;
`;

const LoadMoreRow = styled(TableRow)`
  padding: 1rem 0;
`;

const LoadMoreTableCell = styled(TableCell)`
  border-bottom: none;
  text-align: center;
`;

const Resizer = styled.div`
  width: 2px;
  cursor: col-resize;
  display: block;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  border-right: 2px solid transparent;
  user-select: none;
  &:hover {
    border-color: #ccc;
  }
  &.active {
    border-color: #517ea5;
    width: 2px;
  }
`;

const StyledLoadMoreButton = styled(LoadingButton)``;

const CopyCellWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
`;

const CopyCellContent = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const CopyCellIconButton = styled.span<{ $copied: boolean }>`
  visibility: hidden;
  flex-shrink: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0 2px;
  color: ${props => props.theme.custom.palette.primary900};

  svg {
    font-size: 1.4rem;
  }

  ${CopyCellWrapper}:hover & {
    visibility: visible;
  }
`;

type TableComponents =
  | 'Table'
  | 'TableHead'
  | 'TableBodyCell'
  | 'TableBody'
  | 'TableHeaderCell'
  | 'TableSortLabel'
  | 'TableColumnLabel'
  | 'TableHeaderRow'
  | 'TableBodyRow'
  | 'TableBodySection'
  | 'TableBodyCellInner'
  | 'LoadMoreButton';

export type SectionOption<ItemT> = {
  shouldRender: (currentItem: ItemT, previousItem: ItemT) => boolean;
  render: (
    currentItem: ItemT,
  ) => React.ReactElement | React.ReactElement[] | null | string | undefined;
};

export type SectionOptions<ItemT> = Record<string, SectionOption<ItemT>>;

export interface TableProps<ItemT> {
  className?: string;
  filterable?: boolean;
  filteringOn?: boolean;
  onFilteringToggle?: (isOn: boolean) => void;
  header?: string;
  subHeader?: string;
  headerIcon?: React.ReactNode;
  headerImage?: string;
  columns: TableColumn<ItemT>[];
  columnsWidth?: Record<string, number>;
  items: ItemT[];
  allItemsLoaded: boolean;
  itemsLoaded: boolean;
  loadItemsPending: boolean;
  loadMoreItemsPending?: boolean;
  infiniteScroll?: boolean;
  infiniteScrollOffset?: number;
  sortField?: keyof ItemT;
  sortOrder?: APISortOrder;
  rowOptionsMenu?: (
    item: ItemT,
    onRowMenuClose: () => void,
  ) => React.ReactElement[];
  isExportPending?: boolean;
  onExportClick?: () => void;
  onLoadMoreClicked?: () => void;
  onNewItemButtonClicked?: () => void;
  onColumnHeaderClicked?: (columnDataId: keyof ItemT) => void;
  onRowClick?: (item: ItemT) => void;
  onBodyRowMouseEnter?: (item: ItemT) => void;
  onBodyRowMouseLeave?: (item: ItemT) => void;
  onColumnFilterChange?: (columnId: string, value: string) => void;
  onAdvancedFilterChange?: (columnId: string, value: FilterValue) => void;
  onFilterReset?: () => void;
  onColumnVisibilityChanged?: (columnId: string, value: boolean) => void;
  components?: {
    [key in TableComponents]?: StyledComponentBase<any, any>;
  };
  tableMinHeight?: string;
  hideHeaders?: boolean;
  headerRowHeight?: string;
  bodyRowHeight?: string;
  isRowActive?: (item: ItemT) => boolean;
  rowHighlightColor?: (item: ItemT) => string | undefined;
  onColumnSizeChanged?: (
    columnId: string,
    width: string,
    tableColumns: (TableColumn<ItemT> & { ref: RefObject<HTMLElement> })[],
  ) => void;
  emptyText?: string;
  loadMoreButtonText?: string;
  hiddenColumns?: TableSettingsT['hiddenColumns'];
  autoAdjustColumnsWidth?: boolean;
  sectionOptions?: SectionOptions<ItemT>;
  highlightSearchResults?: boolean;
  onScrollEnd?: (scrollPosition: number) => void;
  onSaveTableSettings?: (columns: TableColumn<ItemT>[]) => void;
  onColumnOrderChanged?: (columns: TableColumn<ItemT>[]) => void;
  onResetTableSettings?: () => void;
  scrollPosition?: number;
  draggable?: boolean;
  headerChildren?: React.ReactNode;
}

export const LastColumnCellStyles = css`
  &:before {
    display: none;
  }
`;

const lastColumn: TableColumn<any> = {
  dataId: SPAN_COLUMN_ID,
  label: '',
  width: '',
  hidden: false,
  order: 1,
  align: 'right',
  sortable: false,
  // @ts-ignore
  cellStyles: LastColumnCellStyles,
};

const getColumnWidth = <T,>(
  column: Pick<TableColumn<any>, 'dataId' | 'minWidth' | 'width'>,
  columnsWidth: TableProps<T>['columnsWidth'],
) => {
  return (
    columnsWidth?.[column.dataId.toString()] || column.width || column.minWidth
  );
};

export const Table = <
  ItemT extends {
    id: number | string;
    highlights?: Record<string, string[] | null> | undefined;
  },
>({
  className,
  filterable = false,
  filteringOn,
  onFilteringToggle,
  header,
  subHeader,
  headerIcon,
  headerImage,
  columns,
  items,
  allItemsLoaded,
  itemsLoaded,
  loadItemsPending,
  loadMoreItemsPending,
  sortField,
  sortOrder,
  isExportPending = false,
  rowOptionsMenu,
  onExportClick,
  onLoadMoreClicked,
  onNewItemButtonClicked,
  onColumnFilterChange,
  onAdvancedFilterChange,
  onFilterReset,
  onColumnVisibilityChanged,
  onColumnHeaderClicked,
  onRowClick,
  onColumnOrderChanged,
  onBodyRowMouseEnter,
  onBodyRowMouseLeave,
  components = {},
  hideHeaders,
  tableMinHeight = '0',
  headerRowHeight = '6.6rem',
  bodyRowHeight = '20',
  isRowActive,
  rowHighlightColor,
  emptyText,
  loadMoreButtonText,
  infiniteScroll = false,
  hiddenColumns = [],
  onColumnSizeChanged,
  infiniteScrollOffset = INFINITE_SCROLL_BOTTOM_OFFSET_PX,
  autoAdjustColumnsWidth = false,
  sectionOptions,
  highlightSearchResults,
  onScrollEnd,
  scrollPosition,
  columnsWidth,
  onSaveTableSettings,
  onResetTableSettings,
  draggable = false,
  headerChildren,
}: TableProps<ItemT>) => {
  const TableComponent: React.ElementType = components.Table || StyledTable;
  const TableHeaderRowComponent: React.ElementType =
    components.TableHeaderRow || StyledTableRow;
  const TableBodyRowComponent: React.ElementType =
    components.TableBodyRow || StyledTableRow;
  const TableBodySectionComponent: React.ElementType =
    components.TableBodySection || StyledTableSection;
  const TableHeadComponent: React.ElementType =
    components.TableHead || TableHead;
  const TableBodyComponent: React.ElementType =
    components.TableBody || StyledTableBody;
  const TableHeaderCellComponent: React.ElementType =
    components.TableHeaderCell || StyledTableHeaderCell;
  const TableSortLabelComponent: React.ElementType =
    components.TableSortLabel || TableSortLabel;
  const TableColumnLabelComponent: React.ElementType =
    components.TableColumnLabel || StyledTableColumnLabel;
  const TableBodyCellComponent: React.ElementType =
    components.TableBodyCell || StyledTableBodyCell;
  const TableBodyCellInnerComponent: React.ElementType =
    components.TableBodyCellInner || StyledTableBodyCellInner;
  const LoadMoreButtonComponent: React.ElementType =
    components.LoadMoreButton || StyledLoadMoreButton;

  const { t } = useTranslation();

  const onRowMenuClicked = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: ItemT) => {
      setOpenedMenuOrder(item);
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const onRowMenuClose = useCallback(() => {
    setOpenedMenuOrder(null);
    setAnchorEl(null);
  }, []);

  const tableColumns = useMemo(() => {
    const initArray = rowOptionsMenu
      ? [
          {
            ...getOptionColumn(onRowMenuClicked),
            ref: React.createRef<HTMLDivElement>(),
          },
        ]
      : [];

    return [
      ...initArray,
      ...columns.map(it => ({
        ...it,
        ref: React.createRef<HTMLDivElement>(),
      })),
    ];
  }, [columns, onRowMenuClicked, rowOptionsMenu]);

  const [tableWidth, setTableWidth] = useState<null | number>(null);
  const [tableHeight, setTableHeight] = useState<null | number>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openedMenu, setOpenedMenuOrder] = React.useState<null | ItemT>(null);
  const [copiedCellId, setCopiedCellId] = useState<string | null>(null);

  const handleCopyCellValue = useCallback(
    (e: React.MouseEvent, cellId: string, text: string) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text);
      setCopiedCellId(cellId);
      setTimeout(
        () => setCopiedCellId(prev => (prev === cellId ? null : prev)),
        2000,
      );
    },
    [],
  );

  const tableRef = useCallbackRef<HTMLDivElement | null>(null, tableEl => {
    if (tableEl) {
      setTableWidth(tableEl.getBoundingClientRect().width);
    }
  });

  const isTableEmpty = items?.length === 0;

  const showEmptyText =
    items?.length === 0 && itemsLoaded && !loadItemsPending && emptyText;

  const hasLoadMoreButton = onLoadMoreClicked && itemsLoaded && !allItemsLoaded;

  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.getBoundingClientRect().height);
    }
  }, [items.length, tableRef]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!infiniteScroll || !hasLoadMoreButton || !loadMoreRowRef.current)
      return;

    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (
            !entry.isIntersecting ||
            loadMoreItemsPending ||
            !hasLoadMoreButton
          )
            continue;
          onLoadMoreClicked?.();
        }
      },
      {
        root: root && root.scrollHeight > root.clientHeight + 10 ? root : null,
        rootMargin: `0px 0px ${infiniteScrollOffset}px 0px`,
        threshold: 0,
      },
    );
    observer.observe(loadMoreRowRef.current);
    return () => observer.disconnect();
  }, [
    infiniteScroll,
    hasLoadMoreButton,
    loadMoreItemsPending,
    infiniteScrollOffset,
    onLoadMoreClicked,
  ]);

  useEffect(() => {
    if (scrollPosition === 0) {
      scrollContainerRef.current?.scrollTo({ top: 0 });
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (!onScrollEnd) return;
    const element = scrollContainerRef.current;
    if (!element) return;
    const handler = _.debounce(() => onScrollEnd(element.scrollTop), 400);
    element.addEventListener('scroll', handler);
    return () => {
      element.removeEventListener('scroll', handler);
      handler.cancel?.();
    };
  }, [onScrollEnd]);

  const visibleColumns = useMemo(() => {
    return hiddenColumns?.length
      ? tableColumns.reduce<typeof tableColumns>((result, it) => {
          return hiddenColumns.includes(it.dataId.toString())
            ? result
            : [...result, it];
        }, [])
      : tableColumns;
  }, [tableColumns, hiddenColumns]);

  const fullWidthColspan = useMemo(() => {
    const isTableResizable = _.some(columns, { resizable: true });
    // resizable table has one extra column for smooth resize
    return (
      _.reject(visibleColumns, { hidden: true }).length +
      (isTableResizable ? 1 : 0)
    );
  }, [columns, visibleColumns]);

  const onColumnMouseDown = (index: string | null) => {
    setActiveColumn(index);
  };

  const onResizeMouseMove = useCallback(
    (event: MouseEvent) => {
      tableColumns.forEach(it => {
        if (it.dataId.toString() == activeColumn && it.ref.current) {
          const width =
            event.clientX - it.ref.current.getBoundingClientRect().left;

          it.ref.current.style.width =
            Math.max(
              it.minWidth || 0,
              width < COLUMN_RESIZER_WIDTH ? COLUMN_RESIZER_WIDTH : width,
            ) + 'px';
        }
      });
    },
    [activeColumn, tableColumns],
  );

  const sortedColumns = useMemo(() => {
    const columns = visibleColumns;
    const isTableResizable = _.some(columns, { resizable: true });

    // trick: if all width are set, then browser will autosize columns
    // therefore we add one empty column without width, this will make resize smooth
    // and size other columns will not be affected
    return isTableResizable
      ? [...columns, { ...lastColumn, ref: React.createRef<HTMLDivElement>() }]
      : columns;
  }, [visibleColumns]);

  const clearListeners = useCallback(() => {
    window.removeEventListener('mousemove', onResizeMouseMove);
    window.removeEventListener('mouseup', clearListeners);
  }, [onResizeMouseMove]);

  const onResizeMouseUp = useCallback(() => {
    if (activeColumn) {
      const foundColumn = tableColumns.find(
        it => it.dataId.toString() === activeColumn,
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      foundColumn?.ref?.current &&
        onColumnSizeChanged?.(
          activeColumn,
          foundColumn.ref.current.style.width,
          tableColumns,
        );
    }
    setActiveColumn(null);
    clearListeners();
  }, [activeColumn, clearListeners, tableColumns, onColumnSizeChanged]);

  useEffect(() => {
    if (autoAdjustColumnsWidth && tableWidth) {
      const totalColumnsWidth = visibleColumns.reduce((result, column) => {
        return result + Number(getColumnWidth(column, columnsWidth));
      }, 0);
      if (totalColumnsWidth < tableWidth) {
        _.forEach(visibleColumns, column => {
          if (column.ref.current) {
            column.ref.current.style.width =
              Math.max(
                column.minWidth || 0,
                (Number(getColumnWidth(column, columnsWidth)) *
                  (tableWidth - SCROLL_WIDTH)) /
                  totalColumnsWidth,
              ) + 'px';
          }
        });
      }
    }
  }, [tableWidth, autoAdjustColumnsWidth, visibleColumns, columnsWidth]);

  useEffect(() => {
    if (activeColumn !== null) {
      window.addEventListener('mousemove', onResizeMouseMove);
      window.addEventListener('mouseup', onResizeMouseUp);
    }

    return () => {
      clearListeners();
    };
  }, [activeColumn, onResizeMouseMove, onResizeMouseUp, clearListeners]);

  // a list that remembers the filter row, like it remembers the columns, controls it
  const [internalFilteringOn, setInternalFilteringOn] = useState(false);
  const isFilteringOn = filteringOn ?? internalFilteringOn;

  return (
    <>
      <PageHeader
        header={header}
        subHeader={subHeader}
        icon={headerIcon}
        image={headerImage}
      >
        <Box minHeight="3.4rem">
          {headerChildren}
          {filterable && (
            <>
              {onFilterReset && columns.some(hasActiveColumnFilter) && (
                <Tooltip title={t('table.clearFilters')} arrow>
                  <Button variant="text" onClick={onFilterReset}>
                    <CloseCircle variant="TwoTone" size="24" />
                  </Button>
                </Tooltip>
              )}
              {onNewItemButtonClicked && (
                <Tooltip title={t('table.addNew')} arrow>
                  <Button variant="text" onClick={onNewItemButtonClicked}>
                    <Add variant="TwoTone" size="24" />
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                title={
                  isFilteringOn
                    ? t('table.hideFilters')
                    : t('table.showFilters')
                }
                arrow
              >
                <Button
                  variant="text"
                  onClick={() => {
                    setInternalFilteringOn(!isFilteringOn);
                    onFilteringToggle?.(!isFilteringOn);
                  }}
                >
                  {isFilteringOn ? (
                    <Filter variant="TwoTone" size="24" />
                  ) : (
                    <FilterRemove variant="TwoTone" size="24" />
                  )}
                </Button>
              </Tooltip>
            </>
          )}
          {onExportClick && (
            <Tooltip title={t('table.export')} arrow>
              <LoadingButton
                loading={isExportPending}
                variant="text"
                onClick={onExportClick}
              >
                <DocumentDownload variant="TwoTone" size="24" />
              </LoadingButton>
            </Tooltip>
          )}
          {onColumnVisibilityChanged && (
            <TableSettings
              columns={tableColumns}
              onColumnVisibilityChanged={onColumnVisibilityChanged}
              draggable={draggable}
              onSaveTableSettings={onSaveTableSettings}
              onColumnOrderChanged={onColumnOrderChanged}
              onResetTableSettings={onResetTableSettings}
            />
          )}
        </Box>
      </PageHeader>

      <TableWrapper className={className} minheight={tableMinHeight}>
        <StyledTableContainer ref={scrollContainerRef}>
          <TableComponent stickyHeader aria-label="sticky table" ref={tableRef}>
            <TableHeadComponent>
              <TableHeaderRowComponent>
                {sortedColumns.map(column =>
                  column.hidden ? null : (
                    <TableHeaderCellComponent
                      key={column.dataId.toString()}
                      ref={column.ref}
                      align={column.align}
                      style={{
                        padding: '0.5rem 0.35rem 0.5rem 0.35rem',
                        width: getColumnWidth(column, columnsWidth),
                        ...(column.fixed && {
                          ...fixedStyles,
                          top: 0,
                          zIndex: 4,
                        }),
                      }}
                      headerheight={hideHeaders ? '0' : headerRowHeight}
                    >
                      {hideHeaders ? null : onColumnHeaderClicked &&
                        column.sortable !== false ? (
                        <TableSortLabelComponent
                          hideSortIcon={!column.dataId || isTableEmpty}
                          active={
                            !isTableEmpty &&
                            itemsLoaded &&
                            sortField === column.dataId
                          }
                          style={
                            isTableEmpty
                              ? emptyTableSortHeaderStyles
                              : { userSelect: 'none' }
                          }
                          direction={sortOrder?.toLowerCase()}
                          onClick={
                            !isTableEmpty && column.dataId
                              ? () =>
                                  onColumnHeaderClicked?.(
                                    column.dataId as keyof ItemT,
                                  )
                              : undefined
                          }
                        >
                          {
                            // @ts-ignore
                            t(column.label) || ''
                          }
                        </TableSortLabelComponent>
                      ) : (
                        <TableColumnLabelComponent variant="h3">
                          {
                            // @ts-ignore
                            t(column.label) || ''
                          }
                        </TableColumnLabelComponent>
                      )}
                      {isFilteringOn && column.dataId != SPAN_COLUMN_ID && (
                        <Box height="1.3rem">
                          {!column.notFilterable &&
                            (column.customFilter ? (
                              <column.customFilter
                                value={column.filter}
                                filterValue={column.filterValue}
                                columnId={column.dataId}
                                onFilterChange={onColumnFilterChange}
                                onAdvancedFilterChange={onAdvancedFilterChange}
                              />
                            ) : column.filterType === 'numeric' ? (
                              <AdvancedNumericFilter
                                columnId={column.dataId}
                                filterValue={column.filterValue}
                                onAdvancedFilterChange={onAdvancedFilterChange}
                              />
                            ) : column.filterType === 'string' ? (
                              <AdvancedStringFilter
                                columnId={column.dataId}
                                filterValue={column.filterValue}
                                onAdvancedFilterChange={onAdvancedFilterChange}
                              />
                            ) : column.filterType === 'date' ? (
                              <AdvancedDateFilter
                                columnId={column.dataId}
                                filterValue={column.filterValue}
                                onAdvancedFilterChange={onAdvancedFilterChange}
                                dateOnly
                              />
                            ) : column.filterType === 'dateTime' ? (
                              <AdvancedDateFilter
                                columnId={column.dataId}
                                filterValue={column.filterValue}
                                onAdvancedFilterChange={onAdvancedFilterChange}
                              />
                            ) : (
                              <StringFilter
                                columnId={column.dataId}
                                value={column.filter}
                                onFilterChange={onColumnFilterChange}
                              />
                            ))}
                        </Box>
                      )}
                      <Resizer
                        style={{
                          display: column.resizable ? 'block' : 'none',
                          height: items.length ? tableHeight || 0 : '100%',
                        }}
                        onMouseDown={() =>
                          onColumnMouseDown(column.dataId.toString())
                        }
                        className={
                          activeColumn === column.dataId.toString()
                            ? 'active'
                            : 'idle'
                        }
                      />
                    </TableHeaderCellComponent>
                  ),
                )}
              </TableHeaderRowComponent>
            </TableHeadComponent>
            {!loadItemsPending && (
              <TableBodyComponent>
                {items.map((item, index) => {
                  const section =
                    sectionOptions?.[(sortField || '').toString()];
                  const shouldRenderSection = section?.shouldRender(
                    item,
                    items?.[index - 1],
                  );
                  const sectionElement = section?.render?.(item);

                  return [
                    ...[
                      shouldRenderSection
                        ? [
                            <TableBodySectionRow key={`section-${item.id}`}>
                              <TableBodySectionComponent
                                height={bodyRowHeight}
                                top={hideHeaders ? '0' : headerRowHeight}
                                colSpan={fullWidthColspan}
                              >
                                {isTitleString(sectionElement) ? (
                                  <StyledTableSectionText>
                                    {sectionElement}
                                  </StyledTableSectionText>
                                ) : (
                                  sectionElement
                                )}
                              </TableBodySectionComponent>
                            </TableBodySectionRow>,
                          ]
                        : [],
                    ],
                    <TableBodyRowComponent
                      key={item.id}
                      className={classNames({
                        active: isRowActive?.(item),
                      })}
                      onClick={() => onRowClick?.(item)}
                      onMouseEnter={() => onBodyRowMouseEnter?.(item)}
                      onMouseLeave={() => onBodyRowMouseLeave?.(item)}
                      item={item}
                      rowhighlightcolor={rowHighlightColor?.(item)}
                    >
                      {sortedColumns.map(column => {
                        if (column.hidden) {
                          return null;
                        }
                        const rawValue = column.dataId
                          ? _.get(item, column.dataId)
                          : '';
                        // @ts-ignore TODO: find out the right constraint for `anything with .toString() method`
                        const { value = rawValue?.toString(), element = null } =
                          column.formatter?.(
                            rawValue,
                            item,
                            highlightSearchResults
                              ? item.highlights?.[
                                  column.highlightKey ||
                                    (column.dataId as string)
                                ]
                              : undefined,
                          ) || { value: rawValue };

                        const cellId = column.dataId.toString() + item.id;
                        const isCopied = copiedCellId === cellId;
                        const copyValue =
                          value != null ? String(value) : undefined;

                        return (
                          <TableBodyCellComponent
                            key={column.dataId.toString() + item.id}
                            align={column.align}
                            height={bodyRowHeight}
                            cellstyles={column.cellStyles}
                            style={{
                              padding: '0.6rem 0.35rem 0.6rem 0.35rem',
                              ...(column.fixed && {
                                ...fixedStyles,
                                backgroundColor: 'inherit',
                              }),
                            }}
                          >
                            <CopyCellWrapper>
                              <CellTooltip
                                placement="top-start"
                                enterDelay={TOOLTIP_APPEAR_DELAY}
                                enterNextDelay={TOOLTIP_APPEAR_DELAY}
                                title={value}
                              >
                                <CopyCellContent>
                                  {element || (
                                    <TableBodyCellInnerComponent variant="p">
                                      {value}
                                    </TableBodyCellInnerComponent>
                                  )}
                                </CopyCellContent>
                              </CellTooltip>
                              {copyValue && (
                                <Tooltip
                                  title={
                                    isCopied
                                      ? t('table.copied')
                                      : t('table.copy')
                                  }
                                  placement="top"
                                >
                                  <CopyCellIconButton
                                    $copied={isCopied}
                                    onClick={e =>
                                      handleCopyCellValue(e, cellId, copyValue)
                                    }
                                  >
                                    {isCopied ? (
                                      <TickCircle variant="TwoTone" size="24" />
                                    ) : (
                                      <Copy variant="TwoTone" size="24" />
                                    )}
                                  </CopyCellIconButton>
                                </Tooltip>
                              )}
                            </CopyCellWrapper>
                          </TableBodyCellComponent>
                        );
                      })}
                    </TableBodyRowComponent>,
                  ];
                })}
                {showEmptyText && (
                  <EmptyResultRow>
                    <EmptyResultCell colSpan={fullWidthColspan}>
                      {emptyText}
                    </EmptyResultCell>
                  </EmptyResultRow>
                )}
                {hasLoadMoreButton && (
                  <LoadMoreRow
                    ref={loadMoreRowRef}
                    data-testid="table-load-more-row"
                  >
                    <LoadMoreTableCell colSpan={fullWidthColspan}>
                      <LoadMoreButtonComponent
                        loading={loadMoreItemsPending}
                        disabled={loadMoreItemsPending}
                        onClick={onLoadMoreClicked}
                      >
                        {loadMoreButtonText}
                      </LoadMoreButtonComponent>
                    </LoadMoreTableCell>
                  </LoadMoreRow>
                )}
              </TableBodyComponent>
            )}
            {loadItemsPending && (
              <TableBodyComponent>
                {Array.from(
                  { length: Math.max(30, items.length) },
                  (_, index) => {
                    const skeletonItem = { id: `skeleton-${index}` };
                    return [
                      <TableBodyRowComponent
                        id={`row-${skeletonItem.id}`}
                        key={skeletonItem.id}
                        item={skeletonItem}
                      >
                        {sortedColumns.map(column => {
                          if (column.hidden) {
                            return null;
                          }
                          return (
                            <TableBodyCellComponent
                              id={`cell-${String(column.dataId)}-${
                                skeletonItem.id
                              }`}
                              key={String(column.dataId) + skeletonItem.id}
                              align={column.align}
                              height={bodyRowHeight}
                              cellstyles={column.cellStyles}
                              style={{
                                padding: '0.6rem 0.35rem 0.6rem 0.35rem',
                                ...(column.fixed && {
                                  ...fixedStyles,
                                  backgroundColor: 'inherit',
                                }),
                              }}
                            >
                              <CopyCellWrapper>
                                <CopyCellContent>
                                  <TableBodyCellInnerComponent variant="p">
                                    <Skeleton variant="text" />
                                  </TableBodyCellInnerComponent>
                                </CopyCellContent>
                              </CopyCellWrapper>
                            </TableBodyCellComponent>
                          );
                        })}
                      </TableBodyRowComponent>,
                    ];
                  },
                )}
              </TableBodyComponent>
            )}
          </TableComponent>
        </StyledTableContainer>
        {/* Skeleton loading state */}
        {openedMenu && (
          <Menu
            id={`long-menu-for-}`}
            anchorEl={anchorEl}
            open={!!openedMenu}
            onClose={onRowMenuClose}
          >
            {rowOptionsMenu?.(openedMenu, onRowMenuClose)}
          </Menu>
        )}
      </TableWrapper>
    </>
  );
};
